import type { Character } from "@cloudnest/horror-race-engine";
import {
  ALL_CHARACTERS,
  clampRacePosition,
} from "@cloudnest/horror-race-engine";
import type { CSSProperties } from "react";
import { HorrorCharacterChip } from "../HorrorCharacterChip";
import { Pawn } from "./Pawn";

/**
 * Shared race-board view (T-D2) — the presentational 5-lane board core extracted
 * from the in-game RaceTrack so the recap screens can reuse the SAME design
 * instead of re-implementing their own board.
 *
 * Pure/props-only: 5 FIXED lanes (one per `ALL_CHARACTERS`) over the physical
 * cadence ruler (출발 · 1..N grouped in 4s · 골인 marker); a pawn sits in its
 * engine position cell. Screen-specific testids (`horror-lane`/`horror-pawn`
 * in-game) are props so each consumer keeps its own e2e contract.
 */

// T21: physical-board cadence — cells are NOT evenly spaced. They cluster in
// groups of 4 with a small gap between groups and a big gap framing 출발/골인:
//   라벨 | 출발 ── 1·2·3·4 ─ 5·6·7·8 ─ 9·10·11·12 ── 골인
const GROUP_SIZE = 4;
const GAP_BIG = "0.5rem";
const GAP_SMALL = "0.2rem";

type TrackColumn =
  | { readonly type: "cell"; readonly idx: number }
  | { readonly type: "gap"; readonly size: "big" | "small" };

// Column layout shared by the header ruler and every lane so they stay aligned.
// idx 0 = 출발, 1..trackLength = numbered cells, trackLength + 1 = visual 골인.
function trackColumns(trackLength: number): readonly TrackColumn[] {
  const cols: TrackColumn[] = [
    { type: "cell", idx: 0 },
    { type: "gap", size: "big" },
  ];
  for (let n = 1; n <= trackLength; n += 1) {
    cols.push({ type: "cell", idx: n });
    if (n % GROUP_SIZE === 0 && n < trackLength) {
      cols.push({ type: "gap", size: "small" });
    }
  }
  cols.push(
    { type: "gap", size: "big" },
    { type: "cell", idx: trackLength + 1 },
  );
  return cols;
}

function gridTemplate(cols: readonly TrackColumn[]): string {
  // The label column holds the icon-over-name chip, so it needs room for the
  // character name (e.g. 뱀파이어) below the glyph. Cells flex to fit the width.
  const track = cols
    .map((c) =>
      c.type === "cell"
        ? "minmax(0, 1fr)"
        : c.size === "big"
          ? GAP_BIG
          : GAP_SMALL,
    )
    .join(" ");
  return `minmax(3.2rem, auto) ${track}`;
}

type CellKind = "start" | "goal" | "cell";

function cellKind(idx: number, trackLength: number): CellKind {
  if (idx === 0) return "start";
  if (idx === trackLength + 1) return "goal";
  return "cell";
}

function cellStyle(kind: CellKind, height: number): CSSProperties {
  const base: CSSProperties = {
    minWidth: 0,
    height,
    borderRadius: 3,
  };
  if (kind === "goal") {
    return {
      ...base,
      border:
        "1px solid color-mix(in srgb, var(--cn-horror-finished-flash) 50%, transparent)",
      background:
        "repeating-linear-gradient(45deg, var(--cn-horror-finish-stripe-dark) 0 3px, color-mix(in srgb, var(--cn-horror-finish-stripe-light) 30%, var(--cn-horror-track)) 3px 6px)",
    };
  }
  if (kind === "start") {
    return {
      ...base,
      border: "1px dashed var(--cn-horror-text-muted)",
      backgroundColor: "var(--cn-horror-surface-secondary)",
    };
  }
  return {
    ...base,
    border: "1px solid var(--cn-horror-lane-divider)",
    backgroundColor: "var(--cn-horror-track)",
  };
}

interface HeaderRulerProps {
  readonly cols: readonly TrackColumn[];
  readonly trackLength: number;
}

function HeaderRuler({ cols, trackLength }: HeaderRulerProps) {
  return (
    <div
      className="grid items-center gap-[2px]"
      style={{ gridTemplateColumns: gridTemplate(cols) }}
    >
      <div />
      {cols.map((col, i) => {
        if (col.type === "gap") {
          return <div key={`gap-${i}`} aria-hidden="true" />;
        }
        const kind = cellKind(col.idx, trackLength);
        if (kind === "start") {
          return (
            <div
              key="start"
              data-testid="track-start"
              className="text-center font-bold text-[8px]"
              style={{ color: "var(--cn-horror-text-muted)" }}
            >
              출발
            </div>
          );
        }
        if (kind === "goal") {
          return (
            <div
              key="goal"
              data-testid="track-goal"
              className="text-center font-bold text-[8px]"
              style={{ color: "var(--cn-horror-text-muted)" }}
            >
              골인
            </div>
          );
        }
        return (
          <div
            key={`cell-${col.idx}`}
            data-testid={`track-cell-${col.idx}`}
            className="text-center font-mono text-[8px]"
            style={{ color: "var(--cn-horror-text-muted)" }}
          >
            {col.idx}
          </div>
        );
      })}
    </div>
  );
}

interface LaneProps {
  readonly character: Character;
  readonly position: number;
  readonly cols: readonly TrackColumn[];
  readonly trackLength: number;
  readonly laneTestId: string;
  readonly pawnTestId: string;
}

function Lane({
  character,
  position,
  cols,
  trackLength,
  laneTestId,
  pawnTestId,
}: LaneProps) {
  return (
    <div
      data-testid={laneTestId}
      data-character={character}
      className="grid items-center gap-[2px]"
      style={{ gridTemplateColumns: gridTemplate(cols) }}
    >
      <div className="flex items-center justify-center overflow-hidden">
        <HorrorCharacterChip
          character={character}
          iconSize={13}
          labelSize={8}
        />
      </div>
      {cols.map((col, i) => {
        if (col.type === "gap") {
          return <div key={`gap-${i}`} aria-hidden="true" />;
        }
        const kind = cellKind(col.idx, trackLength);
        const hasPawn = position === col.idx;
        return (
          <div
            key={`cell-${col.idx}`}
            className="flex items-center justify-center"
            style={cellStyle(kind, 18)}
          >
            {hasPawn && (
              <Pawn
                character={character}
                position={position}
                testId={pawnTestId}
                className="shrink-0"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

interface RaceBoardViewProps {
  readonly positions: Readonly<Record<Character, number>>;
  readonly trackLength: number;
  /** Lane testid per consumer (in-game `horror-lane`). */
  readonly laneTestId?: string;
  /** Pawn testid per consumer (in-game `horror-pawn`). */
  readonly pawnTestId?: string;
  /** Show the 출발/번호/골인 header ruler. */
  readonly showRuler?: boolean;
}

export function RaceBoardView({
  positions,
  trackLength,
  laneTestId = "horror-lane",
  pawnTestId = "horror-pawn",
  showRuler = true,
}: RaceBoardViewProps) {
  const cols = trackColumns(trackLength);
  return (
    <div className="flex flex-col gap-[3px]">
      {showRuler && <HeaderRuler cols={cols} trackLength={trackLength} />}
      {ALL_CHARACTERS.map((character) => (
        <Lane
          key={character}
          character={character}
          position={clampRacePosition(positions[character], trackLength)}
          cols={cols}
          trackLength={trackLength}
          laneTestId={laneTestId}
          pawnTestId={pawnTestId}
        />
      ))}
    </div>
  );
}
